import { useEffect, useMemo, useRef, useState } from "react";
import { History } from "lucide-react";
import { useOrbState, ORB_LABELS } from "@/lib/orb";
import { unlockAudio } from "@/lib/audio";
import { AuroraOrb } from "@/components/AuroraOrb";
import { HeartToggle } from "@/components/HeartToggle";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { MessageList } from "./MessageList";
import { CommandBar } from "./CommandBar";
import { HistoryPanel } from "./HistoryPanel";
import { Footer } from "@/components/Footer";
import { useAuroraChat } from "@/hooks/useAuroraChat";
import { useVoiceControls } from "@/hooks/useVoiceControls";

const DOT = { parlato: "bg-amber-400", elaborazione: "bg-violet-400", ascolto: "bg-cyan-300" };
const isMobile = () => window.innerWidth < 640;

const Greeting = ({ name }) => (
  <div className="mt-3 flex flex-col items-center text-center" data-testid="greeting">
    <h1 className="font-heading font-bold uppercase tracking-wide text-4xl sm:text-6xl leading-tight wake-glow text-aurora" data-testid="greeting-title">
      Ciao{name ? ` ${name}` : ""}, sono Aurora
    </h1>
    <p className="text-base sm:text-lg text-slate-300 mt-3" data-testid="greeting-subtitle">Come posso aiutarti oggi? <span className="text-cyan-300">Scrivi nel prompt</span></p>
    <p className="text-xs text-slate-500 mt-1.5">oppure tocca il microfono per dettare</p>
  </div>
);

const orbSize = (compact) => {
  const mobile = isMobile();
  if (compact) return mobile ? 84 : 120;
  return Math.min(420, Math.max(220, window.innerWidth * (mobile ? 0.66 : 0.5)));
};

const OrbHud = ({ compact, onTalk }) => {
  const st = useOrbState();
  const size = orbSize(compact);
  return (
    <div className={`flex ${compact ? "flex-row gap-3" : "flex-col"} items-center justify-center`}>
      <button onClick={onTalk} data-testid="btn-orb-talk" aria-label="Detta ad Aurora" className="rounded-full">
        <AuroraOrb size={size} />
      </button>
      <p className="hud-tag flex items-center gap-2" data-testid="orb-state-label"><span className={`w-1.5 h-1.5 rounded-full pulse-dot ${DOT[st] || "bg-emerald-400"}`} />{ORB_LABELS[st]}</p>
    </div>
  );
};

export const Assistant = ({ user, refreshUser, goBilling }) => {
  const settings = useMemo(() => user.settings || {}, [user.settings]);
  const active = user.subscription_active;
  const [input, setInput] = useState("");
  const [sheet, setSheet] = useState(false);
  const scrollRef = useRef(null);
  const chat = useAuroraChat({ refreshUser });
  const { messages, busy, convs, convId } = chat;
  const voice = useVoiceControls({
    send: chat.send, settings,
    appendInput: (text) => setInput((v) => (v ? `${v} ${text}` : text)),
  });

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const sendInput = () => { const t = input; setInput(""); chat.send(t); };
  const talk = () => { unlockAudio(); if (active) voice.toggleDictate(); };
  const openConv = async (id) => { await chat.openConv(id); setSheet(false); };
  const newConv = () => { chat.newConv(); setSheet(false); };

  const history = <HistoryPanel conversations={convs} activeId={convId} onOpen={openConv} onNew={newConv} onDelete={chat.delConv} />;
  const empty = messages.length === 0;

  return (
    <div className="flex h-full" data-testid="assistant-view">
      <aside className="hidden lg:block w-72 shrink-0 border-r border-cyan-500/10 p-4 bg-[#030712]/40 backdrop-blur-xl">{history}</aside>
      <div className="flex-1 flex flex-col min-w-0 relative">
        <div className="lg:hidden absolute top-2 left-3 z-20">
          <Sheet open={sheet} onOpenChange={setSheet}>
            <SheetTrigger asChild>
              <button data-testid="btn-open-history" title="Cronologia" className="btn-ghost-hud rounded-full w-9 h-9 flex items-center justify-center"><History size={16} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#050b18] border-cyan-500/20 w-80 pt-10">
              <SheetTitle className="sr-only">Cronologia</SheetTitle>
              {history}
            </SheetContent>
          </Sheet>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8">
          <div className="max-w-3xl mx-auto py-3">
            {empty ? (
              <div className="pt-2 sm:pt-8 flex flex-col items-center">
                <OrbHud onTalk={talk} />
                <Greeting name={user.name?.split(" ")[0]} />
                <span className="hidden sm:inline-flex mt-3"><HeartToggle /></span>
              </div>
            ) : (
              <>
                <div className="sticky top-0 z-10 flex justify-center pb-4 -mx-2 bg-gradient-to-b from-[#030712] via-[#030712]/95 to-[#030712]/0 [mask-image:linear-gradient(to_bottom,black_75%,transparent)]"><OrbHud compact onTalk={talk} /></div>
                <MessageList messages={messages} busy={busy} onRegenerate={chat.regenerate} voice={settings.tts_voice} />
              </>
            )}
          </div>
        </div>
        <div className="px-3 sm:px-8 pb-[max(env(safe-area-inset-bottom),6px)] pt-2">
          <div className="max-w-3xl mx-auto">
            {!active && (
              <button onClick={goBilling} data-testid="paywall-banner" className="w-full mb-2 rounded-xl border border-amber-400/40 bg-amber-500/10 text-amber-200 text-sm py-2.5 px-4 text-left">
                Il tuo periodo di prova è terminato. <span className="underline">Attiva l'abbonamento</span> per continuare.
              </button>
            )}
            <CommandBar value={input} onChange={setInput} onSend={sendInput}
              busy={busy} onStop={chat.stop} dictating={voice.dictating} onDictate={talk} disabled={!active} />
            <div className="hidden sm:block"><Footer compact /></div>
          </div>
        </div>
      </div>
    </div>
  );
};
